import { Box, Checkbox, FormControlLabel, Radio, RadioGroup, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { FC } from "react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { usePtoTrimmedSegments } from "../../hooks/useTimeOffImpact";
import { INK } from "../../theme/tokens";
import { getStaffingDate } from "../../utils/dateUtils";
import type { DayOff } from "../../utils/models";
import SystemDialog from "../UIToolkit/Popups/Dialog";

type DeletePTODialogProps = {
	dayOff: DayOff | null;
	nurseId: number | null;
	isLoading?: boolean;
	onConfirm: (restoreRecurringShifts: boolean, restoreTrimmedShifts: boolean) => void;
	onCancel: () => void;
	zIndex?: number;
};

export const DeletePTODialog: FC<DeletePTODialogProps> = ({ dayOff, nurseId, isLoading, onConfirm, onCancel, zIndex }) => {
	const [restoreRecurringShifts, setRestoreRecurringShifts] = useState(true);
	// DEFAULT: `true` mirrors the server's own default for `restore_trimmed_shifts`, so confirming
	// before (or without) the trimmed-segments answer produces exactly the behaviour the API would
	// have chosen on its own. A failed check is told to the user rather than left to look like
	// "nothing to restore" — see the error caption below.
	const [restoreTrimmedShifts, setRestoreTrimmedShifts] = useState(true);
	const { chosenDepartment } = useHospitalData();
	const { t } = useTranslation();

	// Offer the restore choice (DAT-230 design-doc §7) only when this PTO actually trimmed or split
	// shifts. The server owns that lookup, including its segment slack, so the raw PTO window goes
	// over the wire unpadded. Hidden while the answer is in flight, which
	// is the same rendering as "nothing to restore" — safe because the state default above matches
	// what the server would do anyway.
	const trimmedSegmentsWindow = useMemo(() => (dayOff ? { start: dayOff.dateFrom, end: dayOff.dateTo } : null), [dayOff]);
	const { hasTrimmedSegments, isError: isTrimmedSegmentsError } = usePtoTrimmedSegments(
		chosenDepartment.department_id,
		nurseId,
		trimmedSegmentsWindow,
		!!dayOff && nurseId !== null,
	);

	if (!dayOff) return null;

	const start = getStaffingDate(dayOff.dateFrom, chosenDepartment.dayStart);
	const end = getStaffingDate(dayOff.dateTo.minus({ minutes: 1 }), chosenDepartment.dayStart);

	const dateRange = start.hasSame(end, "day")
		? start.toFormat("ccc, MMM d, yyyy")
		: `${start.toFormat("ccc, MMM d, yyyy")} - ${end.toFormat("ccc, MMM d, yyyy")}`;

	return (
		<SystemDialog
			open={!!dayOff}
			onClose={onCancel}
			title="Delete Day Off"
			dataTestId="delete-pto-dialog"
			zIndex={zIndex}
			confirmDisabled={isLoading}
			confirmLoading={isLoading}
			body={
				<Box>
					<Box
						sx={{
							mb: 2,
						}}
					>
						Are you sure you want to delete this? on dates: {dateRange}
					</Box>
					{isTrimmedSegmentsError && (
						<Typography sx={{ color: alpha(INK.SECONDARY, 0.7), fontSize: "13px", mb: 1 }} data-testid="delete-pto-restore-mode-error" role="alert">
							{t("delete.pto.trimmed.check.failed")}
						</Typography>
					)}
					{hasTrimmedSegments && (
						<Box sx={{ mb: 1 }} data-testid="delete-pto-restore-mode">
							<Typography sx={{ color: alpha(INK.SECONDARY, 0.7), fontSize: "13px" }}>{t("delete.pto.trimmed.title")}</Typography>
							<RadioGroup value={restoreTrimmedShifts ? "restore" : "keep"} onChange={(event) => setRestoreTrimmedShifts(event.target.value === "restore")}>
								<FormControlLabel value="restore" control={<Radio data-testid="delete-pto-restore-mode-restore" />} label={t("delete.pto.restore.original")} />
								<FormControlLabel value="keep" control={<Radio data-testid="delete-pto-restore-mode-keep" />} label={t("delete.pto.keep.current")} />
							</RadioGroup>
						</Box>
					)}
					<FormControlLabel
						control={<Checkbox checked={restoreRecurringShifts} onChange={(e) => setRestoreRecurringShifts(e.target.checked)} />}
						label="Restore missing recurring shifts"
					/>
				</Box>
			}
			onConfirm={() => onConfirm(restoreRecurringShifts, restoreTrimmedShifts)}
		/>
	);
};
